// Sprog til Digitalisering (værktøjer, dialoger, prompts, hjælp og rapporter).
//
// REGEL: ALLE tekster, brugeren kan se, skal gå igennem t() eller T(), og have en engelsk oversættelse i en.js. Se I18N.md.
//
// Dansk tekst er nøglen (gettext-stil): koden indeholder den danske tekst, og ordbøgerne (en.js, ...) oversætter den. Kendes teksten ikke
// på det aktive sprog, vises den danske (for sv/de/jp/pt først den engelske - samme fallback som frontendens vue-i18n).
//
//   t("Luk")                          -> "Luk" / "Close"
//   t("Fandt {0} af {1}", n, total)   -> {0}, {1} ... erstattes (også i oversættelsen, så rækkefølgen kan ændres)
//   T("Linje")                        -> markerer en tekst til oversættelse, UDEN at slå den op nu (værktøjsdefinitioner mv., der oprettes,
//                                        før sproget kendes). Selve opslaget sker, når teksten læses (se localizeFields) eller via t().
//
// Sproget sættes af værten (PointView-frontenden: Potree.setDigitizationLanguage(this.$i18n.locale)); standard er dansk.

import en from "./en.js";
import de from "./de.js";
import sv from "./sv.js";
import pt from "./pt.js";
import jp from "./jp.js";

const dictionaries = {en, de, sv, pt, jp};
let locale = "da";
const listeners = new Set();

const norm = (lang) => String(lang || "da").toLowerCase().split(/[-_]/)[0];

export function registerDictionary(lang, entries){
	dictionaries[norm(lang)] = Object.assign(dictionaries[norm(lang)] || {}, entries);
}

export function setLanguage(lang){
	const next = norm(lang);
	if(next === locale) return;
	locale = next;
	for(const fn of Array.from(listeners)) fn(locale);
}

export function getLanguage(){
	return locale;
}

// Lyt efter sprogskift (fx for at bygge åbne paneler om). Returnerer en afmelding.
export function onLanguageChange(fn){
	listeners.add(fn);
	return () => listeners.delete(fn);
}

function lookup(text){
	if(locale === "da") return text;
	const own = dictionaries[locale];
	if(own && Object.prototype.hasOwnProperty.call(own, text)) return own[text];
	const english = dictionaries.en;
	if(english && Object.prototype.hasOwnProperty.call(english, text)) return english[text];
	return text;
}

function fill(text, args){
	if(!args.length) return text;
	return text.replace(/\{(\d+)\}/g, (match, i) => (i < args.length && args[i] !== undefined ? String(args[i]) : match));
}

export function t(text, ...args){
	if(typeof text !== "string" || text === "") return text;
	return fill(lookup(text), args);
}

// Sprogkode til tal- og datoformatering (toLocaleString m.fl.) ud fra det aktive sprog
const NUMBER_LOCALES = {da: "da-DK", en: "en-GB", sv: "sv-SE", de: "de-DE", pt: "pt-PT", jp: "ja-JP", ja: "ja-JP"};
export function numberLocale(){
	return NUMBER_LOCALES[locale] || "en-GB";
}

// Markér til oversættelse (identitet): se øverst.
export function T(text){
	return text;
}

// Oversætter en værdi: tekst slås op; en liste eller et almindeligt objekt (fx instructions: {direction1: T("..."), ...}) bliver en visning,
// der oversætter sine tekster, når de læses; alt andet (funktioner, tal) er uændret.
function translateValue(value){
	if(typeof value === "string") return t(value);
	if(Array.isArray(value) || (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype)){
		return new Proxy(value, {
			get(target, prop, receiver){
				const v = Reflect.get(target, prop, receiver);
				return typeof prop === "string" ? translateValue(v) : v;
			},
		});
	}
	return value;
}

// Oversætter en hel datastruktur (liste/objekt med T()-tekster) ved læsning - til hjælp, genveje og andre faste tabeller
export function localized(value){
	return translateValue(value);
}

// Gør de angivne felter på et værktøjs-/kategoriobjekt "levende": læses de, slås den danske tekst op på det aktive sprog.
// (Definitionerne skrives som T("...") og oprettes ved indlæsning af modulet, før sproget er sat.)
export function localizeFields(object, fields){
	for(const field of fields){
		const raw = object[field];
		if(raw === undefined || raw === null || typeof raw === "function") continue;
		Object.defineProperty(object, field, {
			get: () => translateValue(raw),
			// en senere tildeling (fx tool.name = "...") erstatter den levende værdi med en almindelig
			set: (value) => Object.defineProperty(object, field, {value, writable: true, enumerable: true, configurable: true}),
			enumerable: true,
			configurable: true,
		});
	}
	return object;
}

// Kun til test: de danske nøgler, der findes i en ordbog
export function dictionaryKeys(lang){
	return Object.keys(dictionaries[norm(lang)] || {});
}
